import _ from 'lodash';
import moment from 'moment';
import { z } from 'zod';

const permittedPeriodOptions = [
  'withinTheNext',
  'withinTheLast',
  'thisWeek',
  'nextWeek',
  'lastWeek',
  'thisMonth',
  'nextMonth',
  'lastMonth',
  'thisQuarter',
  'nextQuarter',
  'lastQuarter',
  'thisYear',
  'nextYear',
  'lastYear',
  'today',
  'tomorrow',
  'yesterday',
  'before',
  'after',
  'onExactDate',
  'customDateRange',
];

const limitedPeriodOptions = [
  'withinTheLast',
  'thisWeek',
  'lastWeek',
  'thisMonth',
  'lastMonth',
  'thisQuarter',
  'lastQuarter',
  'thisYear',
  'lastYear',
  'today',
  'yesterday',
  'before',
  'after',
  'onExactDate',
  'customDateRange',
];

const permittedIntervals = ['days', 'weeks', 'months', 'years'];

export const defaultMultiSelectIdsFilterSchema = z.object({
  selectedIds: z.array(z.string().nullable()),
});

export const defaultDateFilterSchema = z.object({
  withinTheNextFrequency: z.string(),
  withinTheNextInterval: z.enum(permittedIntervals),
  withinTheLastFrequency: z.string(),
  withinTheLastInterval: z.enum(permittedIntervals),
  before: z.date().nullable(),
  after: z.date().nullable(),
  onExactDate: z.date().nullable(),
  customDateRangeFrom: z.date().nullable(),
  customDateRangeTo: z.date().nullable(),
});

const dateFilterCustomValidation = (schema, ctx) => {
  if (
    (schema.period === 'withinTheNext' &&
      schema.withinTheNextFrequency.trim().length === 0) ||
    (schema.period === 'withinTheLast' &&
      schema.withinTheLastFrequency.trim().length === 0)
  ) {
    ctx.addIssue({
      code: 'too_small',
      minimum: 1,
      type: 'string',
      inclusive: true,
      path: ['frequency'],
      message: "Frequency can't be blank",
    });
  }

  if (
    (schema.period === 'withinTheNext' &&
      Number(schema.withinTheNextFrequency) > 9999) ||
    (schema.period === 'withinTheLast' &&
      Number(schema.withinTheLastFrequency) > 9999)
  ) {
    ctx.addIssue({
      code: 'too_big',
      minimum: 1,
      type: 'string',
      inclusive: true,
      path: ['frequency'],
      message: 'Frequency is too big',
    });
  }

  if (
    schema.period === 'customDateRange' &&
    moment(schema.customDateRangeTo).isBefore(schema.customDateRangeFrom, 'day')
  ) {
    ctx.addIssue({
      code: 'custom',
      minimum: 1,
      type: 'string',
      inclusive: true,
      path: ['customDateRangeTo'],
      message: "End date can't be before start date",
    });
  }
};

export const lifecycleStatusFilterSchema = z.object({
  live: z.boolean(),
  future: z.boolean(),
  ended: z.boolean(),
  noDates: z.boolean(),
});

export const folderFilterSchema = defaultMultiSelectIdsFilterSchema;

export const lastUpdatedFilterSchema = defaultDateFilterSchema
  .extend({ period: z.enum(limitedPeriodOptions) })
  .superRefine(dateFilterCustomValidation);

export const reviewStatusFilterSchema = z.object({
  pastDueDate: z.boolean(),
  upcoming: z.boolean(),
  upToDate: z.boolean(),
  notSet: z.boolean(),
});

export const reviewDateFilterSchema = defaultDateFilterSchema
  .extend({ period: z.enum([...permittedPeriodOptions]).nullable() })
  .superRefine(dateFilterCustomValidation);

export const approvalStatusFilterSchema = z.object({
  rejected: z.boolean(),
  pendingApproval: z.boolean(),
  approved: z.boolean(),
  notSubmitted: z.boolean(),
});

export const creatorFilterSchema = defaultMultiSelectIdsFilterSchema;

export const creationDateFilterSchema = defaultDateFilterSchema
  .extend({ period: z.enum(limitedPeriodOptions) })
  .superRefine(dateFilterCustomValidation);

export const startDateFilterSchema = defaultDateFilterSchema
  .extend({ period: z.enum([...permittedPeriodOptions]).nullable() })
  .superRefine(dateFilterCustomValidation);

export const signatureStatusFilterSchema = z.object({
  hasRejections: z.boolean(),
  pendingSignatures: z.boolean(),
  fullySigned: z.boolean(),
  notRequested: z.boolean(),
});

export const reviewerFilterSchema = defaultMultiSelectIdsFilterSchema;

export const approverFilterSchema = defaultMultiSelectIdsFilterSchema;
